import { RightCircleFilled } from '@ant-design/icons';
import { Button, Col, Row } from 'antd';
import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import LayoutComponent from '../../components/LayoutComponent/LayoutComponent';
import configuration from '../../configs/IConfiguration';
import CustomContext from '../../context/CustomContext';
import { providerAuthTypes } from '../../model/types';
import cellnexSetupImage from '../../resources/images/cellnex/cellnex-setup-home.svg';
import cellnexSyncImage from '../../resources/images/cellnex/cellnex-sync-home.svg';
import cellnexTasksImage from '../../resources/images/cellnex/cellnex-tasks-home.svg';
import connectionsImage from '../../resources/images/connections.svg';
import connectionPermissionsImage from '../../resources/images/connection-permissions.svg';
import filesAttributesImage from '../../resources/images/files-attributes.svg';
import modelicalSetupImage from '../../resources/images/modelical-setup-home.svg';
import modelicalSyncImage from '../../resources/images/modelical-sync-home.svg';
import modelicalTasksImage from '../../resources/images/modelical-tasks-home.svg';
import rolesService from '../../services/RolesService';
import styles from './HomePage.module.scss';

/**
 * Returns the home page.
 * @returns the home page.
 */
const HomePage = (): React.ReactElement => {
    /*** HOOKS ***/

    const context = useContext(CustomContext);
    const { auth, providerAuths } = context;

    /*** EFFECTS ***/

    /*** METHODS ***/

    /*** COMPONENTS ***/

    return (
        <LayoutComponent pageId="home" style={{ background: '#f2f2f2' }}>
            <div className={styles.pageLayout}>
                <Row gutter={[16, 16]} className={styles.twoCards}>
                    <Col lg={12} md={24} xs={24}>
                        <div className={styles.oneCard}>
                            <div className={styles.littleCard}>
                                <div className={styles.title}>
                                    <FormattedMessage
                                        id="home.welcome.title"
                                        values={{ user: auth?.firstName.toUpperCase() }}
                                    />
                                </div>
                                <div className={styles.description}>
                                    <FormattedMessage id="home.welcome.description" />
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col lg={12} md={24} xs={24}>
                        <div className={styles.oneCard}>
                            <div className={styles.littleCard}>
                                <div className={styles.title}>
                                    <FormattedMessage id="home.instructions.title" />
                                </div>
                                <div className={styles.description}>
                                    <FormattedMessage id="home.instructions.description" />
                                </div>
                                <a href="https://sites.google.com/modelical.com/help-modelical-io/link">
                                    <Button
                                        className={styles.helpButton}
                                        disabled={auth?.organization?.id !== 'modelical'}
                                    >
                                        <FormattedMessage id="button.help" />
                                    </Button>
                                </a>
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row
                    gutter={[16, 16]}
                    className={styles.twoCards}
                    hidden={!providerAuths.some((a) => providerAuthTypes.includes(a))}
                >
                    <Col lg={16} md={24} xs={24}>
                        <div className={styles.oneCard}>
                            <Link to={rolesService.hasAnyRole(auth, ['ROLE_ADMIN', 'ROLE_USER']) ? '/sync' : '#'}>
                                <div
                                    className={`${styles.application} ${
                                        rolesService.hasAnyRole(auth, ['ROLE_ADMIN', 'ROLE_USER'])
                                            ? styles.enabled
                                            : styles.disabled
                                    }`}
                                >
                                    <div className={styles.titleBlue}>
                                        <FormattedMessage id="home.links.title" />
                                        <RightCircleFilled className={`${styles.rightCircle} primary-color`} />
                                    </div>
                                    <div className={styles.description}>
                                        <FormattedMessage id="home.links.description" />
                                    </div>
                                    <CustomImageComponent
                                        cellnex={cellnexSyncImage}
                                        predefined={modelicalSyncImage}
                                        alt={'sync'}
                                    />
                                </div>
                            </Link>
                        </div>
                    </Col>
                    <Col lg={8} md={24} xs={24}>
                        <div className={styles.oneCard}>
                            <div className={styles.littleCard}>
                                <div className={styles.title}>
                                    <FormattedMessage id="home.linksSections.title" />
                                </div>
                                <Link to="/sync/connections">
                                    <div className={styles.sectionWithImage}>
                                        <div>
                                            <img src={connectionsImage} alt={'sync'} />
                                        </div>
                                        <div>
                                            <div className={styles.littleTitle}>
                                                <FormattedMessage id="home.connections.title" />
                                            </div>
                                            <div className={styles.description}>
                                                <FormattedMessage id="home.connections.description" />
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                                {providerAuths.includes('AUTODESK') && (
                                    <Link to="/sync/permissions">
                                        <div className={styles.sectionWithImage}>
                                            <div>
                                                <img src={connectionPermissionsImage} alt={'sync'} />
                                            </div>
                                            <div>
                                                <div className={styles.littleTitle}>
                                                    <FormattedMessage id="home.connectionPermissions.title" />
                                                </div>
                                                <div className={styles.description}>
                                                    <FormattedMessage id="home.connectionPermissions.description" />
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                )}
                                <Link to="/sync/files">
                                    <div className={styles.sectionWithImage}>
                                        <div>
                                            <img src={filesAttributesImage} alt={'sync'} />
                                        </div>
                                        <div>
                                            <div className={styles.littleTitle}>
                                                <FormattedMessage id="home.filesAttributes.title" />
                                            </div>
                                            <div className={styles.description}>
                                                <FormattedMessage id="home.filesAttributes.description" />
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </div>
                        </div>
                    </Col>
                </Row>
                <div className={styles.otherModules}>
                    <FormattedMessage id="home.otherModules" />
                </div>
                <Row gutter={[16, !providerAuths.includes('AUTODESK') ? 0 : 16]} className={styles.twoCards}>
                    <Col lg={12} md={24} xs={24}>
                        <div className={styles.oneCard} hidden={!providerAuths.includes('AUTODESK')}>
                            <Link to={rolesService.hasAnyRole(auth, ['ROLE_ADMIN', 'ROLE_USER']) ? '/setup' : '#'}>
                                <div
                                    className={`${styles.application} ${
                                        rolesService.hasAnyRole(auth, ['ROLE_ADMIN', 'ROLE_USER'])
                                            ? styles.enabled
                                            : styles.disabled
                                    }`}
                                >
                                    <div className={styles.sectionWithImageModules}>
                                        <div>
                                            <CustomImageComponent
                                                cellnex={cellnexSetupImage}
                                                predefined={modelicalSetupImage}
                                                alt={'setup'}
                                                height={true}
                                            />
                                        </div>
                                        <div>
                                            <div className={styles.littleTitle2}>
                                                <FormattedMessage id="home.accSetup.title" />
                                                <RightCircleFilled className={`${styles.rightCircle} primary-color`} />
                                            </div>
                                            <div className={styles.description}>
                                                <FormattedMessage id="home.accSetup.description" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </Col>
                    <Col lg={providerAuths.includes('AUTODESK') ? 12 : 24} md={24} xs={24}>
                        <div className={styles.oneCard}>
                            <Link to={rolesService.hasAnyRole(auth, ['ROLE_ADMIN', 'ROLE_USER']) ? '/tasks' : '#'}>
                                <div
                                    className={`${styles.application} ${
                                        rolesService.hasAnyRole(auth, ['ROLE_ADMIN', 'ROLE_USER'])
                                            ? styles.enabled
                                            : styles.disabled
                                    }`}
                                >
                                    <div className={styles.sectionWithImageModules}>
                                        <div>
                                            <CustomImageComponent
                                                cellnex={cellnexTasksImage}
                                                predefined={modelicalTasksImage}
                                                alt={'tasks'}
                                                height={true}
                                            />
                                        </div>
                                        <div>
                                            <div className={styles.littleTitle2}>
                                                <FormattedMessage id="home.taskManager.title" />
                                                <RightCircleFilled className={`${styles.rightCircle} primary-color`} />
                                            </div>
                                            <div className={styles.description}>
                                                <FormattedMessage id="home.taskManager.description" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </Col>
                </Row>
            </div>
        </LayoutComponent>
    );
};

export default HomePage;

/**
 * Returns the custom image component.
 * @returns the custom image component
 */
const CustomImageComponent = (props: CustomImageProps): React.ReactElement => {
    const { cellnex, predefined, alt, height } = props;
    /*** HOOKS ***/

    /*** COMPONENTS ***/

    if (configuration.getTenant() === 'cellnex') {
        return <img src={cellnex} alt={alt} className={`${styles.image} ${height ? styles.imageHeight : undefined}`} />;
    } else {
        return (
            <img src={predefined} alt={alt} className={`${styles.image} ${height ? styles.imageHeight : undefined}`} />
        );
    }
};

interface CustomImageProps {
    cellnex: any;
    predefined: any;
    alt: string;
    height?: boolean;
}
